import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api from "../../api/api"; // Import your custom API instance
import { PaginationInfo } from "../../types/api";

interface EmployeeContextType {
  employees: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchEmployees: (page?: number, perPage?: number) => Promise<void>;
  addEmployee: (employee: any) => Promise<void>;
  editEmployee: (updatedEmployee: any) => Promise<void>;
  deleteEmployee: (id: number) => Promise<void>;
}

export const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

interface EmployeeProviderProps {
  children: ReactNode;
}

export const EmployeeProvider: React.FC<EmployeeProviderProps> = ({ children }) => {
    const [employees, setEmployees] = useState<any[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const fetchEmployees = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: perPage.toString(),
            });
            const response = await api.get(`/employees?${params.toString()}`);
            
            // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
            let employeesData: any[] = [];
            let paginationData: PaginationInfo | null = null;
            const responseData = response.data;
            
            if (responseData?.success) {
                if (responseData.data?.data && Array.isArray(responseData.data.data)) {
                    employeesData = responseData.data.data;
                    paginationData = responseData.data.pagination || responseData.pagination;
                } else if (responseData.data && Array.isArray(responseData.data)) {
                    employeesData = responseData.data;
                    paginationData = responseData.pagination;
                } else if (Array.isArray(responseData)) {
                    employeesData = responseData;
                }
            } else if (Array.isArray(responseData?.data)) {
                employeesData = responseData.data;
            } else if (Array.isArray(responseData)) {
                employeesData = responseData;
            }
            
            setEmployees(employeesData);
            setPagination(paginationData);
        } catch (error) {
            console.error("Failed to fetch employees:", error);
            setEmployees([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const addEmployee = async (employee: any): Promise<void> => {
        try {
            const response = await api.post("/employees", employee);
            const newEmployee = response.data.data || response.data;
            setEmployees((prev) => [...prev, newEmployee]);
        } catch (error: any) {
            console.error("Failed to add employee:", error);
            throw error.response?.data || error;
        }
    };

    const editEmployee = async (updatedEmployee: any): Promise<void> => {
        if (!updatedEmployee.id) {
          throw new Error("Employee ID is missing.");
        }
        try {
          const response = await api.put(`/employees/${updatedEmployee.id}`, updatedEmployee);
          const updatedEmployeeData = response.data.data || response.data;
          setEmployees((prev) =>
            prev.map((employee) =>
              employee.id === updatedEmployee.id ? updatedEmployeeData : employee
            )
          );
        } catch (error: any) {
          console.error("Failed to edit employee:", error);
          throw error.response?.data || error;
        }
      };

    const deleteEmployee = async (id: number): Promise<void> => {
        try {
            await api.delete(`/employees/${id}`);
            setEmployees((prev) => prev.filter((employee) => employee.id !== id));
        } catch (error) {
            console.error("Failed to delete employee:", error);
            throw error;
        }
    };

    useEffect(() => {
        fetchEmployees(1, 15);
    }, [fetchEmployees]);

    return (
        <EmployeeContext.Provider
            value={{
                employees,
                pagination,
                loading,
                fetchEmployees,
                addEmployee,
                editEmployee,
                deleteEmployee,
            }}
        >
            {children}
        </EmployeeContext.Provider>
    );
};
